export const MS_PER_MINUTE = 60000;

export const dateToTimestamp = (date: Date) => {
  return date.getTime();
};

export const timestampToDate = (timestamp: EpochTimeStamp) => {
  const date = new Date();
  date.setTime(timestamp);
  return date;
};

/**
 * Calculates a future timestamp based on duration in minutes from now.
 * @param durationMinutes Duration in minutes from now.
 * @returns Future timestamp in milliseconds.
 */
export const calculateTimestampAfterMinutes = (
  durationMinutes: number,
): number => Date.now() + durationMinutes * MS_PER_MINUTE;

/**
 * Calculates the remaining minutes until expiration.
 * @param expiration The expiration timestamp (number or ISO string)
 * @returns Remaining minutes (rounded up), minimum 0.
 */
export const calculateRemainingMinutes = (
  expiration: number | string | null | undefined,
): number => {
  const now: number = Date.now();
  if (!expiration) return 0;
  const expirationMs =
    typeof expiration === "string"
      ? new Date(expiration).getTime()
      : expiration;
  const diffMs = expirationMs - now;
  return Math.max(0, Math.ceil(diffMs / MS_PER_MINUTE));
};

/**
 * Checks if a session has expired.
 * @param expiration The expiration timestamp (number or ISO string).
 * @returns True if expired, false otherwise.
 */
export const isExpired = (
  expiration: number | string | null | undefined,
): boolean => {
  const now: number = Date.now();
  if (!expiration) return true;
  const expirationMs =
    typeof expiration === "string"
      ? new Date(expiration).getTime()
      : expiration;
  return now >= expirationMs;
};

/**
 * Returns the current time expressed in the given timezone as a Date object.
 * @param timezone Timezone string (e.g. "Europe/Madrid", "Asia/Singapore").
 * @returns Current local time in the given timezone.
 */
export const getLocalDateForTimezone = (timezone: string): Date => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).formatToParts(now);

  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);

  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
};

/**
 * Formats an ISO date string to local time (24-hour clock).
 */
export function formatLocalTime(iso: string | undefined): string | undefined {
  if (!iso?.trim()) return undefined;

  const date = new Date(iso.trim());
  if (Number.isNaN(date.getTime())) return undefined;

  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
  }).format(date);
}
